import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import {
  FileText,
  Search,
  X,
  Eye,
  CheckCircle,
  XCircle,
  ChevronLeft,
  ChevronRight,
  Download
} from 'lucide-react';
import { ApplicationStatus } from '../../types';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ErrorMessage } from '../../components/ErrorMessage';
import { StatusBadge } from '../../components/StatusBadge';
import { API_URL } from '../../api/client';
import { exportToCSV, formatCurrencyForCSV, formatDateForCSV } from '../../utils/csvExport';
import { Button } from '../../components/Button';
import { CurrencyInput } from '../../components/CurrencyInput';
import { formatCurrency } from '../../utils/currency';

export const ApplicationsList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [applications, setApplications] = useState<any[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedApps, setSelectedApps] = useState<Set<string>>(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [productFilter, setProductFilter] = useState(searchParams.get('product') || '');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [minAmount, setMinAmount] = useState(parseFloat(searchParams.get('minAmount') || '0') || 0);
  const [maxAmount, setMaxAmount] = useState(parseFloat(searchParams.get('maxAmount') || '0') || 0);
  const [startDate, setStartDate] = useState(searchParams.get('startDate') || '');
  const [endDate, setEndDate] = useState(searchParams.get('endDate') || '');
  const [sortBy, setSortBy] = useState(searchParams.get('sortBy') || 'newest');

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    fetchApplications();
  }, [currentPage, statusFilter, productFilter, searchQuery, minAmount, maxAmount, startDate, endDate, sortBy]);

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API_URL}/loan-products`);
      const data = await response.json();
      if (data.success && data.data) {
        setProducts(data.data.products || []);
      }
    } catch (err) {
      console.error('Failed to load products:', err);
    }
  };

  const fetchApplications = async () => {
    setLoading(true);
    try {
      // Build query string with all filters
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('limit', '20');
      if (statusFilter) params.append('status', statusFilter);
      if (productFilter) params.append('product', productFilter);
      if (searchQuery) params.append('search', searchQuery);
      if (minAmount > 0) params.append('minAmount', minAmount.toString());
      if (maxAmount > 0) params.append('maxAmount', maxAmount.toString());
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const response = await fetch(`${API_URL}/admin/applications?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await response.json();

      if (data.success && data.data) {
        const sortedApps = sortApplications(data.data, sortBy);
        setApplications(sortedApps);
        setTotalPages(data.pagination.totalPages);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const sortApplications = (apps: any[], sortType: string) => {
    const sorted = [...apps];
    switch (sortType) {
      case 'newest':
        return sorted.sort((a, b) => new Date(b.submitted_at || b.created_at).getTime() - new Date(a.submitted_at || a.created_at).getTime());
      case 'oldest':
        return sorted.sort((a, b) => new Date(a.submitted_at || a.created_at).getTime() - new Date(b.submitted_at || b.created_at).getTime());
      case 'amount_high':
        return sorted.sort((a, b) => parseFloat(b.amount_requested) - parseFloat(a.amount_requested));
      case 'amount_low':
        return sorted.sort((a, b) => parseFloat(a.amount_requested) - parseFloat(b.amount_requested));
      case 'priority':
        // Priority: Submitted > Under Review > Other statuses, then by date
        const priorityOrder: any = {
          'submitted': 1,
          'under_review': 2,
          'approved': 3,
          'declined': 4,
          'disbursed': 5,
          'draft': 6,
        };
        return sorted.sort((a, b) => {
          const priorityDiff = (priorityOrder[a.status] || 10) - (priorityOrder[b.status] || 10);
          if (priorityDiff !== 0) return priorityDiff;
          return new Date(b.submitted_at || b.created_at).getTime() - new Date(a.submitted_at || a.created_at).getTime();
        });
      default:
        return sorted;
    }
  };

  const handleStatusFilterChange = (status: string) => {
    setStatusFilter(status);
    setCurrentPage(1);
    updateSearchParams({ status });
  };

  const updateSearchParams = (newFilters: Record<string, string>) => {
    const params = new URLSearchParams();
    const allFilters = {
      status: statusFilter,
      product: productFilter,
      search: searchQuery,
      minAmount: minAmount > 0 ? minAmount.toString() : '',
      maxAmount: maxAmount > 0 ? maxAmount.toString() : '',
      startDate,
      endDate,
      ...newFilters,
    };

    Object.entries(allFilters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });

    setSearchParams(params);
  };

  const handleClearFilters = () => {
    setStatusFilter('');
    setProductFilter('');
    setSearchQuery('');
    setMinAmount(0);
    setMaxAmount(0);
    setStartDate('');
    setEndDate('');
    setSortBy('priority');
    setCurrentPage(1);
    setSearchParams({});
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      // Select all applications that can be bulk-processed (submitted or under_review only)
      const selectableApps = applications.filter(
        app => app.status === ApplicationStatus.SUBMITTED || app.status === ApplicationStatus.UNDER_REVIEW
      );
      setSelectedApps(new Set(selectableApps.map(app => app.application_id)));
    } else {
      setSelectedApps(new Set());
    }
  };

  const handleSelectApp = (appId: string) => {
    const newSelected = new Set(selectedApps);
    if (newSelected.has(appId)) {
      newSelected.delete(appId);
    } else {
      newSelected.add(appId);
    }
    setSelectedApps(newSelected);
  };

  const handleBulkAction = async (action: 'approve' | 'decline') => {
    if (selectedApps.size === 0) {
      alert('Please select at least one application');
      return;
    }

    const confirmMessage = action === 'approve'
      ? `Are you sure you want to approve ${selectedApps.size} application(s)?`
      : `Are you sure you want to decline ${selectedApps.size} application(s)?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setBulkActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/admin/applications/bulk-action`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          application_ids: Array.from(selectedApps),
          action: action,
        }),
      });

      const data = await response.json();

      if (data.success) {
        alert(`Successfully ${action}ed ${selectedApps.size} application(s)`);
        setSelectedApps(new Set());
        fetchApplications(); // Refresh the list
      } else {
        throw new Error(data.message || `Failed to ${action} applications`);
      }
    } catch (err: any) {
      setError(err.message || `Failed to ${action} applications`);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const isAllSelectableSelected = () => {
    const selectableApps = applications.filter(
      app => app.status === ApplicationStatus.SUBMITTED || app.status === ApplicationStatus.UNDER_REVIEW
    );
    return selectableApps.length > 0 && selectableApps.every(app => selectedApps.has(app.application_id));
  };

  const getSelectableCount = () => {
    return applications.filter(
      app => app.status === ApplicationStatus.SUBMITTED || app.status === ApplicationStatus.UNDER_REVIEW
    ).length;
  };

  if (loading && applications.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="applications-list-page">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Loan Applications</h1>
          <p>Review, filter, and manage all loan applications submitted by customers</p>
        </div>
      </div>

      {error && <ErrorMessage message={error} onClose={() => setError('')} />}

      <div className="filters">
        <div className="filter-row">
          <div className="filter-group">
            <label htmlFor="search">
              <Search size={16} style={{ display: 'inline', marginRight: '4px' }} />
              Search
            </label>
            <input
              type="text"
              id="search"
              placeholder="Reference or customer name..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          <div className="filter-group">
            <label htmlFor="status">Status:</label>
            <select
              id="status"
              value={statusFilter}
              onChange={(e) => handleStatusFilterChange(e.target.value)}
            >
              <option value="">All Statuses</option>
              <option value={ApplicationStatus.DRAFT}>Draft</option>
              <option value={ApplicationStatus.SUBMITTED}>Submitted</option>
              <option value={ApplicationStatus.UNDER_REVIEW}>Under Review</option>
              <option value={ApplicationStatus.APPROVED}>Approved</option>
              <option value={ApplicationStatus.DECLINED}>Declined</option>
              <option value={ApplicationStatus.DISBURSED}>Disbursed</option>
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="product">Product:</label>
            <select
              id="product"
              value={productFilter}
              onChange={(e) => {
                setProductFilter(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option value="">All Products</option>
              {products.map((product) => (
                <option key={product.product_id} value={product.product_id}>
                  {product.product_name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="filter-row">
          <div className="filter-group">
            <CurrencyInput
              name="minAmount"
              label="Min Amount"
              value={minAmount || 0}
              onChange={(value) => {
                setMinAmount(value);
                setCurrentPage(1);
              }}
              placeholder="Min amount"
              showLabel={true}
            />
          </div>

          <div className="filter-group">
            <CurrencyInput
              name="maxAmount"
              label="Max Amount"
              value={maxAmount || 0}
              onChange={(value) => {
                setMaxAmount(value);
                setCurrentPage(1);
              }}
              placeholder="Max amount"
              showLabel={true}
            />
          </div>

          <div className="filter-group">
            <label htmlFor="startDate">From Date:</label>
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          <div className="filter-group">
            <label htmlFor="endDate">To Date:</label>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setCurrentPage(1);
              }}
            />
          </div>

          <div className="filter-group">
            <label htmlFor="sortBy">Sort By:</label>
            <select
              id="sortBy"
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value);
                updateSearchParams({ sortBy: e.target.value });
              }}
            >
              <option value="priority">Priority (Pending First)</option>
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="amount_high">Highest Amount</option>
              <option value="amount_low">Lowest Amount</option>
            </select>
          </div>

          <div className="filter-group">
            <Button onClick={handleClearFilters} variant="secondary" icon={<X size={16} />}>
              Clear Filters
            </Button>
          </div>
        </div>
      </div>

      {applications.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <FileText size={64} />
          </div>
          <h3>No Applications Found</h3>
          <p>No loan applications match your current filters. Try adjusting your search criteria or clear filters to see all applications.</p>
          <Button onClick={handleClearFilters} variant="primary" icon={<X size={20} />}>
            Clear Filters
          </Button>
        </div>
      ) : (
        <>
          {getSelectableCount() > 0 && (
            <div className="bulk-actions-toolbar">
              <div className="bulk-selection-info">
                <input
                  type="checkbox"
                  checked={isAllSelectableSelected()}
                  onChange={handleSelectAll}
                  id="select-all"
                />
                <label htmlFor="select-all">
                  {selectedApps.size > 0
                    ? `${selectedApps.size} selected`
                    : `Select all (${getSelectableCount()} selectable)`}
                </label>
              </div>
              {selectedApps.size > 0 && (
                <div className="bulk-action-buttons">
                  <Button
                    onClick={() => handleBulkAction('approve')}
                    disabled={bulkActionLoading}
                    variant="success"
                    size="sm"
                    loading={bulkActionLoading}
                    icon={<CheckCircle size={16} />}
                  >
                    Approve Selected ({selectedApps.size})
                  </Button>
                  <Button
                    onClick={() => handleBulkAction('decline')}
                    disabled={bulkActionLoading}
                    variant="danger"
                    size="sm"
                    loading={bulkActionLoading}
                    icon={<XCircle size={16} />}
                  >
                    Decline Selected ({selectedApps.size})
                  </Button>
                  <Button
                    onClick={() => setSelectedApps(new Set())}
                    disabled={bulkActionLoading}
                    variant="secondary"
                    size="sm"
                    icon={<X size={16} />}
                  >
                    Clear Selection
                  </Button>
                </div>
              )}
            </div>
          )}

          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  {getSelectableCount() > 0 && <th style={{ width: '40px' }}></th>}
                  <th>Reference</th>
                  <th>Customer</th>
                  <th>Product</th>
                  <th>Amount</th>
                  <th>Term</th>
                  <th>Status</th>
                  <th>Submitted</th>
                  <th>SLA</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {applications.map((app) => {
                  const isSelectable = app.status === ApplicationStatus.SUBMITTED || app.status === ApplicationStatus.UNDER_REVIEW;
                  const isDraft = app.status === ApplicationStatus.DRAFT;
                  const rowClass = isDraft ? 'draft-row' : (selectedApps.has(app.application_id) ? 'selected-row' : '');
                  return (
                    <tr key={app.application_id} className={rowClass}>
                      {getSelectableCount() > 0 && (
                        <td>
                          {isSelectable ? (
                            <input
                              type="checkbox"
                              checked={selectedApps.has(app.application_id)}
                              onChange={() => handleSelectApp(app.application_id)}
                            />
                          ) : (
                            <span style={{ opacity: 0.3 }}>—</span>
                          )}
                        </td>
                      )}
                      <td>{app.application_reference}</td>
                      <td>
                        {app.customer_name || `${app.first_name || ''} ${app.last_name || ''}`}
                      </td>
                      <td>{app.product_name || 'N/A'}</td>
                      <td>
                        {formatCurrency(app.amount_requested)}
                      </td>
                      <td>{app.loan_term_months} months</td>
                      <td>
                        <StatusBadge status={app.status} />
                      </td>
                      <td>
                        {app.submitted_at
                          ? new Date(app.submitted_at).toLocaleDateString()
                          : 'Not submitted'}
                      </td>
                      <td>
                        {app.hours_since_submission !== null && app.hours_since_submission !== undefined ? (
                          <span className={`sla-badge ${
                            app.hours_since_submission < 24 ? 'sla-good' :
                            app.hours_since_submission < 48 ? 'sla-warning' : 'sla-overdue'
                          }`}>
                            {app.hours_since_submission < 1 ? `${Math.round(app.hours_since_submission * 60)}m` :
                             app.hours_since_submission < 24 ? `${Math.round(app.hours_since_submission)}h` :
                             `${Math.round(app.hours_since_submission / 24)}d`}
                          </span>
                        ) : (
                          <span className="sla-badge sla-na">N/A</span>
                        )}
                      </td>
                      <td>
                        <Button
                          onClick={() => navigate(`/admin/applications/${app.application_id}`)}
                          variant="primary"
                          size="sm"
                          icon={<Eye size={16} />}
                        >
                          View Details
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <Button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                variant="secondary"
                icon={<ChevronLeft size={20} />}
              >
                Previous
              </Button>
              <span className="page-info">
                Page {currentPage} of {totalPages}
              </span>
              <Button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages || loading}
                variant="secondary"
              >
                Next
                <ChevronRight size={20} style={{ marginLeft: '0.5rem' }} />
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
